import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, MessageCircle, Send as SendIcon, ArrowUpRight, ArrowDownLeft, CheckCircle2, XCircle, Clock, Copy } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Painel admin de saúde da integração Twilio (SMS + WhatsApp).
 * Mostra últimos 20 envios com status/erro + contagens 24h + código do Sandbox.
 *
 * Rótulos exibidos em português — a chave interna (`delivered`, `sent`, …)
 * segue a nomenclatura oficial da Twilio para preservar compatibilidade com o
 * `comm_logs`, mas o usuário vê "Entregue", "Enviado", "Recebido", "Falhou".
 */
const STATUS_STYLE = {
  delivered: "bg-emerald-50 border-emerald-200 text-emerald-700",
  sent:      "bg-blue-50 border-blue-200 text-blue-700",
  received:  "bg-slate-50 border-slate-300 text-slate-700",
  failed:    "bg-red-50 border-red-200 text-red-700",
  other:     "bg-amber-50 border-amber-200 text-amber-700",
};
const STATUS_LABEL_PT = {
  delivered: "Entregue",
  sent:      "Enviado",
  received:  "Recebido",
  failed:    "Falhou",
  other:     "Outros",
};

const StatusPill = ({ status }) => {
  const s = (status || "other").toLowerCase();
  const cls = STATUS_STYLE[s] || STATUS_STYLE.other;
  const Icon = s === "delivered" ? CheckCircle2 : s === "failed" ? XCircle : s === "received" ? ArrowDownLeft : Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${cls}`}>
      <Icon className="w-3 h-3" strokeWidth={2.4} /> {STATUS_LABEL_PT[s] || STATUS_LABEL_PT.other}
    </span>
  );
};

export const CommHealthPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/comm-health?limit=20");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar saúde Twilio");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (!data?.sandbox_join_code) return;
    navigator.clipboard.writeText(`join ${data.sandbox_join_code}`);
    toast.success(`"join ${data.sandbox_join_code}" copiado`);
  };

  const items = data?.items || [];
  const counts = data?.counts_24h || {};
  const code = data?.sandbox_join_code || "";

  return (
    <section data-testid="comm-health-panel" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Saúde Twilio</div>
          <h2 className="font-display font-bold text-2xl text-[#0B2B62] mt-1">SMS + WhatsApp em tempo real</h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="comm-health-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      {/* KPIs 7d — rótulos em português (chave técnica preservada como testid) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["delivered", "sent", "received", "failed", "other"].map((k) => (
          <div key={k} className={`p-3 rounded-xl border ${STATUS_STYLE[k]} flex flex-col items-start`} data-testid={`comm-count-${k}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold">{STATUS_LABEL_PT[k]}</div>
            <div className="font-display font-bold text-2xl tabular-nums mt-1">{counts[k] ?? 0}</div>
            <div className="text-[10px] opacity-70">últimos {data?.counts_window_days || 7} dias</div>
          </div>
        ))}
      </div>

      {/* Banner do número/canal WhatsApp ATIVO */}
      {code ? (
        <div className="p-4 border border-emerald-300 bg-emerald-50 rounded-xl flex items-center justify-between gap-3 flex-wrap" data-testid="comm-health-sandbox">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Sandbox WhatsApp ativo</div>
            <div className="text-sm text-slate-700 mt-0.5 break-words">
              Convite atual: <code className="font-mono font-bold">"join {code}"</code> → <code className="font-mono">{data?.wa_from || "—"}</code>
            </div>
          </div>
          <button onClick={copyCode} data-testid="comm-health-copy-code" className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800">
            <Copy className="w-3.5 h-3.5" /> Copiar
          </button>
        </div>
      ) : (
        <div className="p-4 border border-emerald-300 bg-emerald-50 rounded-xl flex items-center gap-3 flex-wrap" data-testid="comm-health-business-mode">
          <MessageCircle className="w-5 h-5 text-emerald-700 shrink-0"/>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">WhatsApp Business ativo</div>
            <div className="text-sm text-slate-700 mt-0.5 break-words">
              Enviando/recebendo de <code className="font-mono font-bold">{data?.wa_from || "—"}</code> — templates aprovados e envios livres dentro da janela de 24h.
            </div>
          </div>
        </div>
      )}

      {/* Tabela de mensagens */}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Canal</th>
                <th className="text-left px-3 py-2 font-bold">Dir</th>
                <th className="text-left px-3 py-2 font-bold">Contato</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Preview</th>
                <th className="text-left px-3 py-2 font-bold">Quando</th>
              </tr>
            </thead>
            <tbody data-testid="comm-health-rows">
              {items.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-sm">
                  {loading ? "Carregando…" : "Nenhuma mensagem registrada ainda."}
                </td></tr>
              ) : items.map((m, i) => {
                const isWa = m.channel === "whatsapp";
                const isOut = (m.direction || "outbound") !== "inbound";
                const contact = isOut ? m.to : m.from;
                const when = m.at || m.created_at;
                return (
                  <tr key={`${m.message_id || i}-${when}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold ${isWa ? "text-emerald-700" : "text-blue-700"}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> {isWa ? "WhatsApp" : "SMS"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {isOut
                        ? <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><ArrowUpRight className="w-3 h-3" /> out</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold"><ArrowDownLeft className="w-3 h-3" /> in</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700 max-w-[180px] truncate">{(contact || "").replace("whatsapp:", "")}</td>
                    <td className="px-3 py-2"><StatusPill status={m.status} /></td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-[280px] truncate" title={m.preview}>{m.preview || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {when ? new Date(when).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
        <SendIcon className="w-3 h-3" /> Fonte: <code className="font-mono">comm_logs</code>. Atualização manual — clique em "Atualizar" ou troque de aba e volte.
      </div>
    </section>
  );
};

export default CommHealthPanel;
